/**
 * Medical Test Service
 * Handles all medical test-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';

class MedicalTestService {
  /**
   * Get list of medical tests
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search query (optional)
   * @param {string} params.category - Filter by category (optional)
   * @returns {Promise<Object>} Response with tests array
   */
  async getMedicalTests(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.category) queryParams.append('category', params.category);

      const response = await axios.get(
        `${API_ENDPOINTS.MEDICAL_TESTS.LIST}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get medical test packages
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search query (optional)
   * @returns {Promise<Object>} Response with packages array
   */
  async getMedicalTestPackages(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);

      const response = await axios.get(
        `${API_ENDPOINTS.MEDICAL_TESTS.PACKAGES}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check delivery availability for medical tests
   * @param {number} latitude - Location latitude
   * @param {number} longitude - Location longitude
   * @returns {Promise<Object>} Response with availability and pricing
   */
  async checkDeliveryAvailability(latitude, longitude) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.MEDICAL_TESTS.CHECK_DELIVERY,
        {
          latitude: latitude,
          longitude: longitude,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Book medical test appointment
   * @param {Object} bookingData - Booking data
   * @param {Array<number>} bookingData.test_ids - Array of test IDs (required)
   * @param {string} bookingData.date - Appointment date YYYY-MM-DD (required)
   * @param {string} bookingData.time - Appointment time HH:MM 24-hour format (required)
   * @param {string} bookingData.address - Full address (required)
   * @param {number} bookingData.latitude - Location latitude (required)
   * @param {number} bookingData.longitude - Location longitude (required)
   * @returns {Promise<Object>} Response with booking details and pricing
   */
  async bookMedicalTest(bookingData) {
    try {
      const response = await axios.post(API_ENDPOINTS.MEDICAL_TESTS.BOOK, {
        test_ids: bookingData.test_ids,
        date: bookingData.date,
        time: bookingData.time,
        address: bookingData.address,
        latitude: bookingData.latitude,
        longitude: bookingData.longitude,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Calculate delivery charge
   * @param {number} distanceKm - Distance in kilometers
   * @returns {number} Delivery charge amount
   */
  calculateDeliveryCharge(distanceKm) {
    return distanceKm * 3; // ₹3 per km
  }

  /**
   * Check if location is serviceable
   * @param {number} distanceKm - Distance in kilometers
   * @returns {boolean} Whether location is within service radius
   */
  isServiceable(distanceKm) {
    return distanceKm <= 30; // Maximum 30 km radius
  }

  /**
   * Get user's medical test appointments
   * @returns {Promise<Object>} Response with appointments array
   */
  async getAppointments() {
    try {
      const response = await axios.get(API_ENDPOINTS.MEDICAL_TESTS.APPOINTMENTS);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Cancel medical test appointment
   * @param {number} appointmentId - Appointment ID
   * @param {string} reason - Cancellation reason (optional)
   * @returns {Promise<Object>} Response with cancellation details
   */
  async cancelAppointment(appointmentId, reason = null) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.MEDICAL_TESTS.CANCEL(appointmentId),
        { reason }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reschedule medical test appointment
   * @param {number} appointmentId - Appointment ID
   * @param {string} newDate - New date YYYY-MM-DD
   * @param {string} newTime - New time HH:MM
   * @returns {Promise<Object>} Response with updated appointment
   */
  async rescheduleAppointment(appointmentId, newDate, newTime) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.MEDICAL_TESTS.RESCHEDULE(appointmentId),
        { new_date: newDate, new_time: newTime }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get appointment history
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise<Object>} Response with history and pagination
   */
  async getHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      const response = await axios.get(
        `${API_ENDPOINTS.MEDICAL_TESTS.HISTORY}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generate invoice for appointment
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<Object>} Response with invoice URL
   */
  async generateInvoice(appointmentId) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.MEDICAL_TESTS.GENERATE_INVOICE(appointmentId)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Download invoice for appointment
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise<Blob>} PDF file blob
   */
  async downloadInvoice(appointmentId) {
    try {
      const response = await axios.get(
        API_ENDPOINTS.MEDICAL_TESTS.DOWNLOAD_INVOICE(appointmentId),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const errorMessage = error.response.data.error || error.response.data.message || 'Something went wrong';
      return new Error(errorMessage);
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return new Error('An error occurred. Please try again.');
    }
  }
}

export default new MedicalTestService();
